import React from 'react';
import { formatCurrency, formatDate, getCustomerFullName } from '../utils/format.js';
import { SepaQrImage } from './SepaQrImage.jsx';
import { PDF_THEME, getPdfLayoutConfig } from './pdfTheme.js';
import { calculateDocumentTotals } from '../utils/totals.js';

/** Rechnungs-/Angebots-Inhalt für Druck – Layout wie Referenz-PDF DevSuite Rechnungsmanager. */
export function InvoicePdfContent({ documentData = {}, customer = {}, type = 'invoice', settings = {}, reminderLevel = 0, reminderFee = 0 }) {
  if (!documentData || !documentData.id) return null;
  const items = documentData.items || [];
  const isSmallBusiness = settings?.isSmallBusiness === true
    || settings?.isSmallBusiness === 1
    || settings?.isSmallBusiness === '1'
    || settings?.isSmallBusiness === 'true';
  const totals = calculateDocumentTotals(
    { ...documentData, reminderFee: type === 'invoice' ? reminderFee : 0 },
    { isSmallBusiness, includeReminderFee: type === 'invoice' },
  );
  const discountRatio = Math.abs(totals.subtotalNet) > 0 ? ((totals.subtotalNet - totals.discountNet) / totals.subtotalNet) : 1;
  const reminderFeeNet = type === 'invoice' ? Number(reminderFee) || 0 : 0;

  const isReminder = type === 'invoice' && reminderLevel >= 1;
  const title = isReminder ? `${reminderLevel}. MAHNUNG` : (type === 'invoice' ? 'RECHNUNG' : 'ANGEBOT');
  const primaryColor = settings?.primaryColor || '#2563eb';
  const customerFullName = getCustomerFullName(customer);
  const customerNumber = customer?.customerNumber || (customer?.id != null ? `K-${String(customer.id).padStart(5, '0')}` : null);
  const layout = getPdfLayoutConfig(settings);

  const recipientLines = [];
  if (customer?.name) recipientLines.push(customer.name);
  if (customerFullName) recipientLines.push(customerFullName);
  const addr = (customer?.address || '').trim();
  if (customer?.zip != null && customer?.city != null) {
    if (addr) recipientLines.push(addr);
    recipientLines.push(`${String(customer.zip).trim()} ${String(customer.city).trim()}`);
  } else if (addr) recipientLines.push(addr);

  const notesToBullets = (notes) => (notes && typeof notes === 'string' ? notes.split('\n').map((s) => s.trim()).filter(Boolean) : []);

  return (
    <div
      className="bg-white text-slate-800 text-[10px] leading-snug print:text-[10px] print-content-area flex flex-col"
      style={{
        fontFamily: PDF_THEME.font.family,
        width: PDF_THEME.page.width,
        minHeight: PDF_THEME.page.minHeight,
        boxSizing: 'border-box',
        margin: '0 auto',
        padding: layout.pagePadding,
      }}
    >
      {/* Header: Logo + Titel/Meta */}
      <div className="flex justify-between items-start gap-3 mb-6">
        <div className="shrink-0">
          {settings?.logoUrl ? (
            <img src={settings.logoUrl} alt="" className="h-[140px] w-auto object-contain rounded-md" />
          ) : (
            <div className="w-80 h-[140px] rounded-md flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: primaryColor }}>
              LOGO
            </div>
          )}
        </div>
        <div className="text-right min-w-0">
          <h1 className="text-base font-bold tracking-wide" style={{ color: primaryColor }}>{title}</h1>
          <div className="mt-1.5 inline-block text-left border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/80 min-w-[140px]">
            <div className="flex justify-between gap-3 mb-1">
              <span className="text-[8px] uppercase tracking-wider text-slate-500 shrink-0">{type === 'invoice' ? 'RECHNUNGS-NR.' : 'ANGEBOTS-NR.'}</span>
              <span className="text-[10px] text-slate-800 font-medium truncate" title={documentData.id}>{documentData.id}</span>
            </div>
            {customerNumber && (
              <div className="flex justify-between gap-3 mb-1">
                <span className="text-[8px] uppercase tracking-wider text-slate-500 shrink-0">KUNDENNUMMER</span>
                <span className="text-[10px] text-slate-800">{customerNumber}</span>
              </div>
            )}
            <div className="flex justify-between gap-3 mb-1">
              <span className="text-[8px] uppercase tracking-wider text-slate-500 shrink-0">DATUM</span>
              <span className="text-[10px] text-slate-800">{formatDate(documentData.date)}</span>
            </div>
            {(type === 'quote' ? documentData.expiryDate : documentData.dueDate) && (
              <div className="flex justify-between gap-3">
                <span className="text-[8px] uppercase tracking-wider text-slate-500 shrink-0">{type === 'quote' ? 'GÜLTIG BIS' : 'FÄLLIG BIS'}</span>
                <span className="text-[10px] text-slate-800">{formatDate(type === 'quote' ? documentData.expiryDate : documentData.dueDate)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Empfänger-Box */}
      <div className="mb-5">
        <div className="inline-block bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 max-w-full">
          <div className="text-[8px] uppercase tracking-wider text-slate-500 mb-1">EMPFÄNGER</div>
          <div className="text-[11px] text-slate-800 font-medium">{customer?.name || '–'}</div>
          {recipientLines.slice(1).map((line, i) => (
            <div key={i} className="text-[10px] text-slate-600 mt-0.5">{line}</div>
          ))}
        </div>
      </div>

      <div className="h-px bg-slate-200 my-3" aria-hidden="true" />
      <h2 className="text-[11px] font-semibold text-slate-800 mb-2">Leistungsübersicht</h2>

      {/* Tabellenkopf */}
      <div
        className={layout.tableHeaderClass}
        style={layout.template === 'clean' ? undefined : { backgroundColor: primaryColor }}
      >
        <div className="py-1.5 px-1.5 w-[58%] text-left">Leistung / Beschreibung</div>
        <div className="py-1.5 px-1 w-[14%] text-center">Menge</div>
        <div className="py-1.5 px-1 w-[14%] text-center">Einzelpreis</div>
        <div className="py-1.5 px-1 w-[14%] text-center">Gesamt (netto)</div>
      </div>

      {/* Positionen */}
      {items.map((item, i) => {
        const bullets = notesToBullets(item.notes);
        return (
          <div
            key={i}
            className="print-item-row flex w-full border-x border-b border-slate-200 py-1.5 px-1.5 text-[10px]"
            style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}
          >
            <div className="w-[58%] pr-2">
              <div className="text-[11px] text-slate-800 font-medium mb-0.5">{item.description}</div>
              {bullets.length > 0 && (
                <ul className="text-[9px] text-slate-500 list-disc pl-3.5 space-y-0.5">
                  {bullets.map((b, j) => (
                    <li key={j}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="w-[14%] text-center text-slate-600">{item.quantity}</div>
            <div className="w-[14%] text-center text-slate-800">{formatCurrency(item.price)}</div>
            <div className="w-[14%] text-center text-slate-800 font-medium">{formatCurrency(item.quantity * item.price * discountRatio)}</div>
          </div>
        );
      })}

      {/* Summen-Kästen */}
      <div className="mt-8 flex flex-wrap justify-end gap-4">
        <div className="inline-flex border border-slate-200 rounded-lg bg-slate-50 p-2.5 gap-5">
          <div className="min-w-[220px]">
            <div className="text-[10px] text-slate-600">Zwischensumme: {formatCurrency(totals.subtotalNet)}</div>
            {Math.abs(totals.discountNet) > 0.000001 && <div className="text-[10px] text-slate-600">Rabatt: {formatCurrency(totals.discountNet)}</div>}
            {Math.abs(totals.taxTotal) > 0.000001 && <div className="text-[10px] text-slate-600">USt.: {formatCurrency(totals.taxTotal)}</div>}
            {reminderFeeNet > 0 && <div className="text-[10px] text-slate-600">Mahngebühr: {formatCurrency(reminderFeeNet)}</div>}
            <div className="mt-2 pt-1.5 border-t-2" style={{ borderColor: primaryColor }}>
              <div className="text-[9px] font-bold uppercase" style={{ color: primaryColor }}>GESAMTBETRAG</div>
              <div className="text-sm font-bold" style={{ color: primaryColor }}>{formatCurrency(totals.totalGross)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* QR nur Rechnung */}
      {type === 'invoice' && settings?.iban && (
        <div className="mt-3 flex justify-end">
          <SepaQrImage settings={settings} documentData={documentData} reminderFee={reminderFee} size={56} />
        </div>
      )}

      {/* Block ganz unten */}
      <div className="mt-auto pt-5">
        <div className="text-[10px] text-slate-600">
          {type === 'invoice' ? (
            <p>Zahlbar bis zum <span className="font-semibold text-slate-800">{formatDate(documentData.dueDate)}</span></p>
          ) : (
            <p>Angebot gültig bis zum <span className="font-semibold text-slate-800">{formatDate(documentData.expiryDate)}</span></p>
          )}
        </div>
        <div className="mt-0.5 text-[9px] text-slate-500">
          <p>Alle Preise in Euro, Angebot freibleibend. Es gelten unsere AGB. Zahlungsziel: 14 Tage nach Rechnungsstellung.</p>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-200">
          <div className="text-[9px] text-slate-700 grid grid-cols-4 gap-x-4 gap-y-0.5">
            <div>
              <div className="font-semibold uppercase text-[8px] text-slate-500 mb-0.5">FIRMENDATEN</div>
              <div>
                {[settings?.companyName, settings?.address, settings?.zipCity].filter(Boolean).length
                  ? [settings?.companyName, settings?.address, settings?.zipCity]
                    .filter(Boolean)
                    .map((line, i) => <div key={i}>{line}</div>)
                  : '–'}
              </div>
            </div>
            <div>
              <div className="font-semibold uppercase text-[8px] text-slate-500 mb-0.5">STEUERNUMMER</div>
              <div>
                {settings?.taxId || settings?.vatId ? (
                  <>
                    {settings.taxId && `Steuernr. ${settings.taxId}`}
                    {settings.vatId && (
                      <>
                        {settings.taxId ? ' ' : ''}
                        USt-ID: {settings.vatId}
                      </>
                    )}
                  </>
                ) : (
                  '–'
                )}
              </div>
            </div>
            <div>
              <div className="font-semibold uppercase text-[8px] text-slate-500 mb-0.5">BANKVERBINDUNG</div>
              <div>
                {[settings?.bankName, settings?.iban && `IBAN: ${settings.iban}`, settings?.bic && `BIC: ${settings.bic}`].filter(Boolean)
                  .length
                  ? [settings?.bankName, settings?.iban && `IBAN: ${settings.iban}`, settings?.bic && `BIC: ${settings.bic}`]
                    .filter(Boolean)
                    .map((line, i) => <div key={i}>{line}</div>)
                  : '–'}
              </div>
            </div>
            <div>
              <div className="font-semibold uppercase text-[8px] text-slate-500 mb-0.5">KONTAKT</div>
              <div>
                {[settings?.email, settings?.phone, settings?.website].filter(Boolean).length
                  ? [settings?.email, settings?.phone, settings?.website]
                    .filter(Boolean)
                    .map((line, i) => <div key={i}>{line}</div>)
                  : '–'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
